import { Link } from 'react-router-dom';
import { TabHeader } from '../components/TabHeader';
import { TabSearchStrip } from '../components/TabSearchStrip';
import { TabDiagram } from '../components/TabDiagrams';
import { Footer } from '../components/Footer';

/**
 * "What is DI Deck" — target of the primary "Find more about DI Deck"
 * CTA on the home banner. A comprehensive, marketing-oriented overview
 * of the platform: what it is, why it exists, who it serves, how it
 * deploys. Deliberately broader in shape than a §6 tab; §6 tabs go
 * deep on one capability, this page covers the whole platform at once.
 */
export function WhatIsDiDeckPage(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-sovereign-line bg-gradient-to-br from-brand-purple-dark via-brand-purple to-brand-purple-light px-4 py-12 text-white md:px-8 md:py-20">
          <div className="mx-auto max-w-4xl">
            <nav aria-label="Breadcrumb" className="mb-4 text-xs text-white/70">
              <Link to="/" className="hover:text-white hover:underline">
                Home
              </Link>
              <span className="mx-2" aria-hidden>·</span>
              <span>What is DI Deck</span>
            </nav>
            <h1 className="mb-4 text-3xl font-extrabold leading-tight tracking-tight text-sovereign-950 md:text-4xl lg:text-5xl">
              A sovereign Decision Orchestration Platform
            </h1>
            <p className="max-w-3xl text-base leading-relaxed text-white/90 md:text-lg">
              DI Deck sits on top of your existing enterprise architecture, unifying raw
              data streams, secure collaboration, and AI-driven predictive modeling into a
              single, sovereign desktop cockpit built for institutional leaders.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="rounded-lg bg-white px-5 py-3 text-sm font-bold text-brand-purple shadow-card hover:bg-white/95"
              >
                See service tiers
              </Link>
              <Link
                to="/faq"
                className="rounded-lg border border-white/60 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
              >
                Read the FAQ
              </Link>
              <a
                href="mailto:sales@dideck.com"
                className="rounded-lg border border-white/60 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
              >
                Talk to sales
              </a>
            </div>
          </div>
        </section>

        {/* The problem */}
        <section aria-labelledby="problem" className="px-4 py-12 md:px-8 md:py-16">
          <div className="mx-auto max-w-3xl">
            <h2
              id="problem"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-brand-purple"
            >
              The problem DI Deck was built for
            </h2>
            <p className="mb-6 text-lg leading-relaxed text-ink md:text-xl">
              Modern organizations don&apos;t have a data problem; they have an execution
              lag and decision fatigue problem. Valuable leadership intent is lost in
              fragmented Slack threads, buried emails, unrecorded video calls, and
              isolated ERP dashboards.
            </p>
            <h3 className="mb-4 text-2xl font-bold leading-snug text-ink md:text-3xl">
              Top management wants one thing: an organisation that reacts to the market as
              one coordinated organ — not seven teams talking past each other.
            </h3>
            <div className="space-y-4 text-[15px] leading-relaxed text-ink">
              <p>
                Every leadership team is asked for the same three things — faster decisions,
                better decisions, and decisions that actually execute. What the average
                stack delivers instead is a Slack thread over here, an email chain over
                there, a Salesforce screen you had to open, a Power BI tab you never
                opened, and a Zoom you forgot to record. Leadership intent decays between
                the moment it is expressed and the moment the source system reflects it.
              </p>
              <p>
                The result is a common failure mode dressed up as operational reality:
                three of the top-four priorities never actually got worked on this quarter,
                a €50k procurement mistake committed because the second signer was on
                holiday and the workflow tool did not know, the drift on your key KPI
                tripped its red band before anyone raised it, and the good AI drafts never
                made it out of somebody\'s private ChatGPT tab into a signed decision.
              </p>
              <p>
                Every organisation operating in the AI era needs more than another SaaS
                surface. It needs an orchestration operating system: one place where every
                decision, every draft, every meeting, and every AI call flows through the
                same signature-aware, ledger-anchored fabric — with the sovereignty
                guarantee that comes as a first-class property, not a bolt-on.
              </p>
            </div>

            {/* Platform essence — pulled out of the paragraph flow into a
                callout so it reads as the load-bearing statement of the page. */}
            <aside className="mt-8 rounded-lg border-l-4 border-brand-purple bg-white p-6 shadow-card md:mt-10 md:p-8">
              <p className="text-lg leading-relaxed text-ink md:text-xl">
                Every leadership team in the AI era — whether a growth-stage exec team
                chasing decision velocity, a global enterprise chasing operational
                consistency, or a regulated tenant chasing an audit-grade trail — needs
                more than another SaaS tool. It needs an orchestration operating system:
                a substrate where every decision, every draft, every meeting, and every
                AI call flows through the same signature-aware, ledger-anchored fabric.
              </p>
              <p className="mt-6 text-xl font-extrabold leading-tight text-brand-purple md:text-2xl">
                DI Deck is that orchestration OS — the gold-standard management platform for the AI era.
              </p>
            </aside>
          </div>
        </section>

        {/* What DI Deck is */}
        <section
          aria-labelledby="what"
          className="border-t border-sovereign-line bg-sovereign-850 px-4 py-12 md:px-8 md:py-16"
        >
          <div className="mx-auto max-w-3xl">
            <h2
              id="what"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-brand-purple"
            >
              What DI Deck is
            </h2>
            <p className="mb-6 text-lg leading-relaxed text-ink md:text-xl">
              DI Deck introduces a new category:{' '}
              <strong className="font-bold text-brand-purple">The Decision Orchestration OS</strong>.
              It is a permanent environment that securely maps to your organization&apos;s
              exact managerial hierarchy, converting unstructured data into structured
              corporate actions.
            </p>
            <h3 className="mb-4 text-2xl font-bold leading-snug text-ink md:text-3xl">
              Managers work in one cockpit. Decisions flow through one fabric. The
              organisation learns from every commit.
            </h3>
            <div className="space-y-4 text-[15px] leading-relaxed text-ink">
              <p>
                Every user of DI Deck — a mid-market team lead, a regional VP, a global
                CEO — lands on the Cockpit each morning. Eleven applications inside one
                shell, EN + HE (RTL). Priority Insights at the top: what needs your
                attention right now. Mission Control below: the queue of decisions ranked
                the way you would rank them, produced by a learned ranker that improves
                week over week. Decision Queue on the side: draft outputs waiting for
                your signature. The whole team works on the same surface with the same
                objects — no drift between what you see and what your reports see.
              </p>
              <p>
                Every mission carries typed targets with live RAG bands and pre-threshold
                drift signals. Every decision is a signed, ledger-anchored object that
                routes through your Position Deck automatically — a €50k Procurement
                spend needs both signatures by the mission-class rule, without a workflow
                tool remembering. Every AI-drafted output happens inside GenWork at a
                chosen sensitivity tier. Every meeting is captured through
                the Synapse and turns into structured decisions + action items you
                distribute after human confirmation.
              </p>
              <p>
                Underneath all of it, the platform is watching itself get better. The
                Sovereign Machine Learning Layer sits inside your boundary and — opt-in
                stage by opt-in stage — replaces third-party embeddings, watches KPIs for
                drift before the band trips, learns your Mission-Control ranking
                preferences, stands up an on-tenant LLM for Regulatory-tier work, and
                proposes routing-rule improvements for your team to approve.
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <FoundationCard
                glyph="◧"
                title="The Cockpit"
                slug="cockpit"
                body="One shell where every manager works. Eleven applications, movable windows, live source-system health. Insights, Mission Control, Decisions, Synapse, GenWork, Ledger — all on one surface with the same objects. EN + HE (RTL) shipping."
              />
              <FoundationCard
                glyph="⚖"
                title="The Decision fabric"
                slug="decisions"
                body="Missions typed. Signature Rules per mission class encode who commits what once, enforced everywhere. Cascade routes work down the reports-to graph. Do-It engine commits the mutation in the connected source system with an idempotency key. Every commit ledger-anchored."
              />
              <FoundationCard
                glyph="★"
                title="Insights + Targets"
                slug="insights"
                body="Top-3 priority mission cards, today's brief, RAG-banded targets, pre-threshold drift signals ≥ 24 samples before a band trips. Mission Control ranker learns your priorities. The organisation stays aligned in real time, not at the Monday standup."
              />
            </div>
          </div>
        </section>

        {/* Performance orientation — DI Deck is a performance platform */}
        <section
          aria-labelledby="performance"
          className="border-t border-white/10 bg-brand-purple-dark px-4 py-12 text-white md:px-8 md:py-16"
        >
          <div className="mx-auto max-w-3xl">
            <h2
              id="performance"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-white/70"
            >
              Performance orientation
            </h2>
            <h3 className="mb-4 text-2xl font-bold leading-snug text-white md:text-3xl">
              Every decision a manager takes is bound to their Goals &amp; KPIs — and the
              platform keeps them on track.
            </h3>
            <p className="mb-4 text-base leading-relaxed text-white/90 md:text-lg">
              Every mission on DI Deck carries typed Goals and KPIs. The moment a decision
              commits — a spend approved, a Do-It signed, an action distributed — its
              projected impact re-attributes to the target and the RAG band updates in
              real time. Managers see the influence of today&apos;s actions on this
              week&apos;s numbers — not in a quarterly review, not on a dashboard someone
              refreshes on Monday morning.
            </p>
            <p className="mb-8 text-base leading-relaxed text-white/90 md:text-lg">
              When a target starts to drift off track, the platform does not wait for the
              manager to notice. The Machine Learning Layer fires a pre-threshold signal
              with a projected-breach timestamp, Priority Insights ranks the manager&apos;s
              most impactful decisions of the day, and the Mission Control ranker surfaces
              the next best action. Staying on track is the platform&apos;s job — the
              manager&apos;s job is to pick and sign.
            </p>
            <ul className="grid gap-3 md:grid-cols-2">
              <PerfPoint
                headline="Live RAG bands on every target"
                body="No Monday-morning refresh. The moment a source-system update crosses an amber or red threshold, the mission card flips — for the CEO and the regional VP on the same beat."
              />
              <PerfPoint
                headline="≥ 24-sample drift lead time"
                body="Machine Learning Layer S2 anomaly detector projects a breach ≥ 24 samples before the band trips. Zero false positives on 500 h of stable-with-jitter data — mechanically proven in the platform test suite."
              />
              <PerfPoint
                headline="≥ 15 % precision@5 ranker lift"
                body="Machine Learning Layer S3 learned Mission Control ranker beats the static baseline. A fairness gate blocks biased rankers end-to-end from promotion, so the lift never comes at cohort cost."
              />
              <PerfPoint
                headline="Time-to-commit collapses"
                body="Signature Rules encode who commits what once and enforce them everywhere. The Do-It engine executes the mutation in the source system. What used to be a two-day workflow chain lands the same afternoon."
              />
              <PerfPoint
                headline="Every commit attributed to a target"
                body="The moment a decision commits, its projected impact rolls up into the Mission's KPI. Managers see the influence of today's action on this week's number in the same Cockpit view — no end-of-quarter surprise, no manual reconciliation."
              />
              <PerfPoint
                headline="What-If against historical state"
                body="Replay any decision (or a batch) under an alternate policy against the exact ledger tip that existed at the time. Executives test 'what if we had used the new signature rule' before committing to it."
              />
            </ul>
          </div>
        </section>

        {/* How your day flows through DI Deck */}
        <section
          aria-labelledby="how-it-works"
          className="border-t border-sovereign-line bg-sovereign-900 px-4 py-12 md:px-8 md:py-16"
        >
          <div className="mx-auto max-w-3xl">
            <h2
              id="how-it-works"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-brand-purple"
            >
              How the platform works
            </h2>
            <h3 className="mb-4 text-2xl font-bold leading-snug text-ink md:text-3xl">
              Six mechanics turn a leadership intent into a committed, audited action.
            </h3>
            <p className="mb-8 text-[15px] leading-relaxed text-ink">
              Not features on a spec sheet — mechanics you feel in the day. Each one links
              through to the tab that goes deep on it.
            </p>
            <ol className="space-y-3">
              <MechanicCard
                n={1}
                glyph="★"
                title="Land on Insights"
                slug="insights"
                body="Top-3 priority mission cards by aggregate score. Today\'s brief with RAG-banded target status. Outlook decision-worthy items surfaced inline. The single-glance answer to “what needs my attention right now” — for the CEO and the regional VP alike."
              />
              <MechanicCard
                n={2}
                glyph="◉"
                title="Targets stay live, drift signals fire early"
                slug="targets"
                body="Every mission target typed at declaration (revenue in EUR, headcount as integer, latency in ms) with direction and threshold. RAG bands update as source-system data flows in. The Machine Learning Layer’s S2 anomaly detector signals a projected breach ≥ 24 samples before the band actually trips."
              />
              <MechanicCard
                n={3}
                glyph="⚑"
                title="The Decision Queue routes work through the hierarchy"
                slug="hierarchy"
                body="Position Deck as canonical org record. Signature Rules per mission class encode signature count + magnitude cap + permitted roles once, and enforce them everywhere. Cascade sends missions down the reports-to graph. Parallel roles (co-CEOs, partners, job-shares) first-class."
              />
              <MechanicCard
                n={4}
                glyph="⚖"
                title="Every commit is a signed, ledger-anchored object"
                slug="decisions"
                body="Scope-typed step-up MFA (WebAuthn / Touch ID / TOTP / push) on every commit. Do-It engine executes the mutation in the source system with an idempotency key. Every commit + rollback writes a ledger entry with target-system receipt — reconciliation is a read, not a spreadsheet."
              />
              <MechanicCard
                n={5}
                glyph="◎"
                title="Synapse + GenWork turn work into decisions"
                slug="synapse"
                body="Meetings and calls captured under per-participant consent, sealed inside the tenant Synapse Vault (WORM), summarised through the platform, distributed after human confirmation. GenWork produces AI-drafted output at a chosen sensitivity tier — every draft ledger-anchored, re-openable across devices."
              />
              <MechanicCard
                n={6}
                glyph="◈"
                title="The platform learns from every commit"
                slug="ml-layer"
                body="Sovereign Machine Learning Layer, five opt-in stages inside your boundary. On-tenant embeddings for RAG. Anomaly detector for KPI drift. Learned ranker for Mission Control. On-tenant LLM for Regulatory-tier work. Routing-policy learner that proposes rule adjustments for a human to approve."
              />
            </ol>
          </div>
        </section>

        {/* The sovereign added value — sovereignty reframed as the AI-era wrapper */}
        <section
          aria-labelledby="sovereign-added-value"
          className="border-t border-sovereign-line bg-sovereign-850 px-4 py-12 md:px-8 md:py-16"
        >
          <div className="mx-auto max-w-3xl">
            <h2
              id="sovereign-added-value"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-brand-purple"
            >
              The sovereign added value
            </h2>
            <h3 className="mb-4 text-2xl font-bold leading-snug text-ink md:text-3xl">
              Why orchestration in the AI era must be sovereign — and how DI Deck delivers
              it structurally.
            </h3>
            <p className="mb-8 text-[15px] leading-relaxed text-ink">
              Every mechanic above happens inside your infrastructure. That is not a
              nice-to-have. In the AI era, an orchestration platform that lets prompts leak
              to third-party endpoints, that leaves decision trails outside your control,
              or that cannot answer a regulator without depending on a vendor — is not
              orchestration. It is a liability. DI Deck was engineered from day one so that
              sovereignty is not a bolt-on: it is the substrate every orchestration
              mechanism runs on.
            </p>

            <TabDiagram slug="what-is-dideck" />

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <FoundationCard
                glyph="⛨"
                title="Sovereign Proxy"
                slug="sovereign-proxy"
                body="The single choke point every AI call passes through. Four sensitivity tiers, HMAC-derived redaction, per-endpoint contract descriptors, cost + budget enforcement, fail-closed routing. Vendor cannot see traffic."
              />
              <FoundationCard
                glyph="☰"
                title="Immutable Ledger"
                slug="ledger"
                body="Per-tenant, hash-chained, 25+ action classes. Signed export bundles with an independent verifier that reruns from Genesis on the regulator\'s own machine. Optional external anchor to S3 Object Lock (WORM)."
              />
              <FoundationCard
                glyph="§"
                title="EU AI Act mapping"
                slug="eu-ai-act"
                body="Articles 6, 9, 10, 12, 13, 14, 15 all map to concrete platform capabilities the code makes good on. DPIA templates auto-populate from tenant configuration. Conformity file auto-populates from ledger data."
              />
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section aria-labelledby="who" className="border-t border-sovereign-line px-4 py-12 md:px-8 md:py-16">
          <div className="mx-auto max-w-3xl">
            <h2
              id="who"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-brand-purple"
            >
              Who DI Deck is for
            </h2>
            <h3 className="mb-4 text-2xl font-bold leading-snug text-ink md:text-3xl">
              Every organisation operating in the AI era.
            </h3>
            <p className="mb-6 text-[15px] leading-relaxed text-ink">
              DI Deck is built to be the gold-standard management platform for
              organisations that want faster, more efficient decisions inside a sovereign
              environment they control. That is a universal need in the AI era — small
              teams and global enterprises alike benefit from the same orchestration
              spine. The value is most acute in the segments below, but the platform is
              designed to serve any organisation pursuing operational excellence with AI
              in the loop.
            </p>
            <ul className="space-y-3 text-[15px] leading-relaxed text-ink">
              <AudienceItem
                title="Every enterprise adopting AI at scale"
                body="Where decision velocity, operational integrity, and reproducibility compound over time. DI Deck makes AI-mediated decisions a durable asset — every draft, every signature, every outcome ledger-anchored — so an organisation\'s decision surface gets better with use, not messier."
              />
              <AudienceItem
                title="Growth-stage + mid-market organisations"
                body="Where consistent, evidenced decision-making becomes a competitive advantage as headcount and scope grow. Signature Rules encode judgement once and enforce it everywhere; the Cockpit gives managers a shared work surface instead of a scattered tool stack."
              />
              <AudienceItem
                title="Regulated finance + insurance"
                body="Banks, asset managers, insurers under Basel, MiFID II, DORA, or Solvency II. Every AI-influenced decision gets a signed, replayable trail; every model choice enforces a training-forbidden contract."
              />
              <AudienceItem
                title="Healthcare + medical devices"
                body="MDCG SaMD change-control, GDPR Article 9 special-category data, patient-safety incident reconstruction. Regulator export produced on tenant-managed media."
              />
              <AudienceItem
                title="Defence + national security"
                body="Full air-gap topology, base-model weights arrive on approved media with hash-verify before load, sovereign LLM inside the tenant\'s data centre."
              />
              <AudienceItem
                title="Public sector + critical infrastructure"
                body="Sovereign-cloud residency; per-tenant Postgres; no vendor-side data path; every routing decision + every promotion is an independently-verifiable ledger entry."
              />
              <AudienceItem
                title="EU AI Act high-risk verticals + multinationals with data-residency needs"
                body="Article 6/9/10/12/13/14/15 all map to concrete platform capabilities. DPIA templates auto-populate from tenant configuration; conformity file auto-populates from ledger data. Cross-border routing rules follow the tenant\'s jurisdiction map."
              />
            </ul>
          </div>
        </section>

        {/* How it deploys */}
        <section
          aria-labelledby="deploy"
          className="border-t border-sovereign-line bg-sovereign-850 px-4 py-12 md:px-8 md:py-16"
        >
          <div className="mx-auto max-w-3xl">
            <h2
              id="deploy"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-brand-purple"
            >
              How DI Deck deploys
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-md border border-sovereign-line bg-white p-5 shadow-card">
                <div className="mb-1 text-sm font-bold text-brand-purple">Cloud service</div>
                <div className="mb-3 text-lg font-bold text-ink">On your cloud project</div>
                <p className="text-sm leading-relaxed text-ink-mute">
                  Deployed to your GCP, AWS, or Azure project — or, at pilot, to the vendor
                  cloud in your chosen region. Managed deploy pipeline; automatic upgrades
                  on your cadence. Zero infrastructure overhead for tenants comfortable with
                  sovereign-cloud residency.
                </p>
              </div>
              <div className="rounded-md border border-sovereign-line bg-white p-5 shadow-card">
                <div className="mb-1 text-sm font-bold text-brand-purple">On-premise service</div>
                <div className="mb-3 text-lg font-bold text-ink">On your hardware</div>
                <p className="text-sm leading-relaxed text-ink-mute">
                  Deployed on tenant hardware in the tenant data centre (Kubernetes or bare-
                  metal VMs). Full air-gap supported. Base-model weights arrive on approved
                  media, hash-verified before load. For defence, national-security, and
                  sovereign-mandate tenants.
                </p>
              </div>
            </div>
            <p className="mt-5 text-center text-sm text-ink-mute">
              Full feature matrix + service scope on the{' '}
              <Link
                to="/pricing"
                className="text-brand-purple underline hover:text-brand-purple-dark"
              >
                Pricing tab
              </Link>
              .
            </p>
          </div>
        </section>

        {/* What's next */}
        <section
          aria-labelledby="next"
          className="border-t border-sovereign-line bg-brand-purple-dark px-4 py-12 text-white md:px-8 md:py-16"
        >
          <div className="mx-auto max-w-3xl text-center">
            <h2
              id="next"
              className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-white/70"
            >
              Where to go next
            </h2>
            <p className="mb-8 text-lg text-white/90 md:text-xl">
              Dig into the capability that matters most to you, or open a pilot conversation.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/cockpit"
                className="rounded-lg bg-white px-5 py-3 text-sm font-bold text-brand-purple shadow-card hover:bg-white/95"
              >
                Explore the Cockpit
              </Link>
              <Link
                to="/eu-ai-act"
                className="rounded-lg border border-white/60 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
              >
                EU AI Act mapping
              </Link>
              <Link
                to="/pricing"
                className="rounded-lg border border-white/60 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
              >
                See pricing
              </Link>
              <a
                href="mailto:sales@dideck.com"
                className="rounded-lg border border-white/60 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
              >
                Talk to sales
              </a>
            </div>
          </div>
        </section>
      </main>

      <TabSearchStrip
        slug="what-is-dideck"
        topic="what DI Deck is"
        chips={[
          'How does DI Deck differ from a general AI gateway?',
          'What data leaves my infrastructure by default?',
          'Which sectors are you piloting with today?',
        ]}
      />
      <Footer />
    </div>
  );
}

function FoundationCard({
  glyph,
  title,
  slug,
  body,
}: {
  glyph: string;
  title: string;
  slug: string;
  body: string;
}): JSX.Element {
  return (
    <Link
      to={`/${slug}`}
      className="flex flex-col rounded-md border border-sovereign-line bg-white p-4 shadow-card hover:border-brand-purple hover:shadow-card-hover"
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          aria-hidden
          className="grid h-8 w-8 place-items-center rounded-md bg-brand-purple text-lg text-white"
        >
          {glyph}
        </span>
        <span className="text-sm font-bold text-ink">{title}</span>
      </div>
      <p className="text-xs leading-relaxed text-ink-mute">{body}</p>
    </Link>
  );
}

function MechanicCard({
  n,
  glyph,
  title,
  slug,
  body,
}: {
  n: number;
  glyph: string;
  title: string;
  slug: string;
  body: string;
}): JSX.Element {
  return (
    <li className="flex gap-4 rounded-md border border-sovereign-line bg-white p-5 shadow-card">
      <div className="flex flex-col items-center gap-1">
        <span
          aria-hidden
          className="grid h-10 w-10 place-items-center rounded-md bg-brand-purple text-lg font-black text-white shadow-card"
        >
          {n}
        </span>
        <span aria-hidden className="text-lg text-brand-purple">{glyph}</span>
      </div>
      <div className="flex-1">
        <div className="mb-1 text-base font-bold text-ink">{title}</div>
        <p className="text-sm leading-relaxed text-ink-mute">{body}</p>
        <Link
          to={`/${slug}`}
          className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brand-purple hover:text-brand-purple-dark hover:underline"
        >
          Deep-dive on {title} →
        </Link>
      </div>
    </li>
  );
}

function PerfPoint({ headline, body }: { headline: string; body: string }): JSX.Element {
  return (
    <li className="rounded-md border border-white/15 bg-white/5 p-4 backdrop-blur">
      <div className="mb-1 text-sm font-bold text-white">{headline}</div>
      <p className="text-xs leading-relaxed text-white/85">{body}</p>
    </li>
  );
}

function AudienceItem({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <li className="rounded-md border border-sovereign-line bg-white p-4 shadow-card">
      <div className="mb-1 text-sm font-bold text-ink">{title}</div>
      <div className="text-sm leading-relaxed text-ink-mute">{body}</div>
    </li>
  );
}
